/* Copyright (c) 2020-present CloudIO Inc */

import { Attribute } from '@cloudio-saas/datasource-types';
import { action, atom, useEvent } from '@cloudio-saas/statex';
import { getUrl } from 'config/config';
import { pageAtom } from 'designer/atoms';
import { usePlatform } from 'platform/PlatformHooks';
import { SQLType } from 'platform/SQL';
import { getUser } from 'root/useUser';
import DSAttributesCache, {
  attributesPath,
  pkAttributesPath,
} from 'store/DSAttributesCache';
import { getURLQueryStringToPost } from 'utils/url';

export interface ObjectRow {
  TABLE_NAME: string;
  TABLE_ROWS?: number;
  TABLE_SCHEMA: string;
  TABLE_TYPE: 'BASE TABLE' | 'VIEW';
  TABLE_NAME_QUOTE?: string;
  COLUMN_NAME_QUOTE?: string;
}

interface Tab {
  name: string;
  id: number;
  type: 'sql' | 'script' | 'full-sql';
}

export const activeTab = atom({
  path: ['sql', 'activeTab', ':appUid'],
  defaultValue: 1,
  urlParam: true,
});

export const filterAtom = atom({
  path: ['sql', 'filter', ':appUid'],
  defaultValue: '',
  urlParam: true,
});

export const tabCounter = atom({
  path: ['sql', 'tabCounter', ':appUid'],
  defaultValue: 1,
  urlParam: true,
});

export const tabsAtom = atom<Tab[]>({
  path: ['sql', 'tabs', ':appUid'],
  defaultValue: [{ name: 'Sheet 1', id: 1, type: 'sql' }],
  urlParam: true,
});

export const tabSQLAtom = atom({
  path: ['sql', ':appUid', ':pageId', 'query'],
  defaultValue: '',
  urlParam: true,
});

export const executeAction = action(
  async (
    { get, set },
    {
      update = false,
      appUid,
      pageId,
      callFetch,
      sql,
    }: {
      update: boolean;
      appUid: string;
      pageId: string;
      callFetch: (
        _appUid: string,
        _sql: string,
        _update?: boolean,
      ) => Promise<SQLType>;
      sql?: string;
    },
  ) => {
    if (!sql) {
      sql = get(tabSQLAtom, { params: { appUid, pageId } });
    }

    if (!sql || sql.trim().length < 12) {
      return;
    }
    set(['sql', appUid, pageId, 'result'], null);
    const json = await callFetch(appUid, sql, update);
    if (json.columns) {
      const attrs = json.columns.map((c) => {
        return {
          attributeCode: c.name,
          attributeType: c.dataType,
          allowBlank: 'Y',
          columnName: c.name,
          appUid,
          attributeName: c.name,
          selectAllowed: 'Y',
          insertAllowed: 'N',
          updateAllowed: 'N',
          queryAllowed: 'Y',
          primaryKey: 'N',
        } as Attribute;
      });
      set<Attribute[]>(attributesPath(appUid, pageId), attrs);
      set<Attribute[]>(pkAttributesPath(appUid, pageId), [
        {
          attributeCode: 'id',
          attributeType: 'String',
          allowBlank: 'N',
          primaryKey: 'Y',
          appUid,
        } as Attribute,
      ]);
      DSAttributesCache.setInitializedAttributes(appUid, pageId);
    }
    set(['sql', appUid, pageId, 'result'], json);
    set(['sql', appUid, pageId, 'id'], '');
    return json;
  },
);

export const createTabAction = action(
  (
    { get, set, call },
    {
      sql,
      name,
      appUid,
      callFetch,
      type = 'sql',
    }: {
      name?: string;
      sql?: string;
      appUid: string;
      type?: 'sql' | 'script' | 'full-sql';
      callFetch?: (
        _appUid: string,
        _sql: string,
        _update?: boolean,
      ) => Promise<SQLType>;
    },
  ) => {
    const c = get(tabCounter, { params: { appUid } }) + 1;
    set(tabCounter, c, { params: { appUid } });
    set(tabsAtom, (t) => [...t, { name: name ?? `Sheet ${c}`, id: c, type }], {
      params: { appUid },
    });
    set(activeTab, c, { params: { appUid } });
    if (sql) {
      set(tabSQLAtom, sql, { params: { appUid, pageId: getPageId(c) } });
      if (callFetch) {
        call(executeAction, {
          appUid,
          pageId: getPageId(c),
          update: false,
          callFetch,
        });
      }
    }
  },
);

export function getPageId(tabId: number) {
  return `sql-${tabId}`;
}

export const removeTabAction = action(
  (
    { get, set, remove },
    { appUid, tabId }: { appUid: string; tabId: number },
  ) => {
    const activeTabId = get(activeTab, { params: { appUid } });
    let tabs = get(tabsAtom, { params: { appUid } });
    const index = tabs.findIndex((tab) => tab.id === tabId);
    tabs = tabs.filter((tab) => tab.id !== tabId);
    set(tabsAtom, tabs, { params: { appUid } });
    const pageId = getPageId(tabId);
    remove(['sql', appUid, pageId]);
    remove(['root', 'dataStore', appUid, pageId]);
    remove(pageAtom, { params: { appUid, pageId } });
    if (activeTabId === tabId) {
      set(activeTab, tabs[tabs.length > index ? index : tabs.length - 1].id, {
        params: { appUid },
      });
    }
  },
);

export function useFetch(): (
  appUid: string,
  sql: string,
  update?: boolean,
) => Promise<SQLType> {
  const platform = usePlatform();
  // this should not re-render as it's used in the MainRoute.tsx
  return useEvent(async (appUid: string, sql: string, update = false) => {
    const user = getUser(platform);
    const response = await fetch(
      `${getUrl()}sql?${getURLQueryStringToPost(user)}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Application': appUid,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sql: sql.trim(), params: [], update }),
      },
    );
    const json = (await response.json()) as SQLType;
    if (json.data) {
      json.data.forEach((r) => {
        r._rs = 'Q';
      });
    }
    return json;
  });
}
